/**
 * ============================================================================
 * KbVersionsDialog — Modal de histórico de versões com diff textual.
 * ----------------------------------------------------------------------------
 * Reúne a timeline (`KbVersionsTimeline`) com um painel de diff entre duas
 * versões selecionadas, baseado no `content_markdown` de cada versão. O
 * padrão de layout segue `ContractTemplateVersionHistoryDialog` (base
 * robusta já usada em contratos), sem depender de libs adicionais.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { GitBranch, History, Plus, Minus, ArrowLeftRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useKbPageVersions } from "@/hooks/kb/useKbEditorial";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  pageId: string | null | undefined;
  pageTitle?: string;
}

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  published: "default",
  approved: "secondary",
  in_review: "outline",
  draft: "outline",
};

/**
 * Diff linha-a-linha muito simples (LCS-based). Suficiente para conteúdo
 * markdown de páginas KB (evita adicionar diff-match-patch como dependência).
 */
function computeLineDiff(a: string, b: string): Array<{ type: "equal" | "added" | "removed"; text: string }> {
  const A = a.split("\n");
  const B = b.split("\n");
  const n = A.length;
  const m = B.length;
  // Tabela de LCS
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: Array<{ type: "equal" | "added" | "removed"; text: string }> = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { out.push({ type: "equal", text: A[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ type: "removed", text: A[i] }); i++; }
    else { out.push({ type: "added", text: B[j] }); j++; }
  }
  while (i < n) { out.push({ type: "removed", text: A[i++] }); }
  while (j < m) { out.push({ type: "added", text: B[j++] }); }
  return out;
}

/** Busca `content_markdown` de duas versões específicas (leitura RLS). */
function useTwoVersionsMarkdown(pageId: string | null | undefined, vA: string | null, vB: string | null) {
  return useQuery({
    queryKey: ["kb", "versions-diff", pageId, vA, vB],
    enabled: !!pageId && !!vA && !!vB,
    queryFn: async () => {
      const ids = Array.from(new Set([vA!, vB!]));
      const { data, error } = await supabase
        .from("kb_page_versions")
        .select("id, content_markdown")
        .in("id", ids);
      if (error) throw error;
      const map = new Map<string, string>();
      (data ?? []).forEach((r: any) => map.set(r.id, r.content_markdown ?? ""));
      return {
        a: map.get(vA!) ?? "",
        b: map.get(vB!) ?? "",
      };
    },
  });
}

export function KbVersionsDialog({ open, onOpenChange, pageId, pageTitle }: Props) {
  const { data: versions, isLoading } = useKbPageVersions(open ? pageId : null);
  const [versionA, setVersionA] = useState<string | null>(null);
  const [versionB, setVersionB] = useState<string | null>(null);

  // Ao carregar as versões, escolhe automaticamente as duas mais recentes.
  useEffect(() => {
    if (!versions || versions.length === 0) return;
    setVersionB((prev) => prev ?? versions[0].id);
    setVersionA((prev) => prev ?? (versions[1]?.id ?? versions[0].id));
  }, [versions]);

  const { data: diffSource, isLoading: isLoadingDiff } = useTwoVersionsMarkdown(pageId, versionA, versionB);

  const diff = useMemo(() => {
    if (!diffSource) return [];
    return computeLineDiff(diffSource.a, diffSource.b);
  }, [diffSource]);

  const stats = useMemo(() => {
    let added = 0, removed = 0;
    diff.forEach((l) => {
      if (l.type === "added") added++;
      else if (l.type === "removed") removed++;
    });
    return { added, removed };
  }, [diff]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] h-[85vh] p-0 flex flex-col gap-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b space-y-1 text-left shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <History className="h-5 w-5" />
            Histórico de versões
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {pageTitle ? `Página "${pageTitle}" — ` : ""}selecione duas versões para comparar as diferenças.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 grid grid-cols-[280px_1fr] overflow-hidden">
          {/* Coluna esquerda: timeline com radios para escolher A e B */}
          <div className="border-r flex flex-col min-h-0">
            <div className="px-3 py-2 border-b text-[11px] text-muted-foreground flex items-center gap-2">
              <ArrowLeftRight className="h-3 w-3" />
              <span>Base</span>
              <span className="ml-auto">Comparar</span>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {isLoading && (
                  <>
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </>
                )}
                {!isLoading && (!versions || versions.length === 0) && (
                  <div className="text-xs text-muted-foreground p-3 text-center">
                    Nenhuma versão registrada.
                  </div>
                )}
                {versions?.map((v: any) => (
                  <div
                    key={v.id}
                    className={cn(
                      "flex items-start gap-2 p-2 rounded-md border text-xs",
                      (v.id === versionA || v.id === versionB) && "border-primary/40 bg-accent/30",
                    )}
                  >
                    <GitBranch className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium">v{v.version_number}</span>
                        <Badge variant={STATUS_VARIANT[v.status] ?? "outline"} className="text-[9px] px-1 py-0">
                          {v.status}
                        </Badge>
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {format(new Date(v.published_at ?? v.reviewed_at ?? v.created_at), "dd MMM yyyy HH:mm", { locale: ptBR })}
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <input
                        type="radio"
                        name="versionA"
                        title="Definir como base"
                        checked={v.id === versionA}
                        onChange={() => setVersionA(v.id)}
                        className="h-3 w-3 cursor-pointer accent-muted-foreground"
                      />
                      <input
                        type="radio"
                        name="versionB"
                        title="Definir para comparar"
                        checked={v.id === versionB}
                        onChange={() => setVersionB(v.id)}
                        className="h-3 w-3 cursor-pointer accent-primary"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Coluna direita: diff */}
          <div className="flex flex-col min-h-0">
            <div className="px-4 py-2 border-b flex items-center gap-3 text-xs shrink-0">
              <span className="font-medium">Diferenças</span>
              <Badge variant="outline" className="gap-1 text-[10px]">
                <Plus className="h-3 w-3 text-emerald-600" /> {stats.added}
              </Badge>
              <Badge variant="outline" className="gap-1 text-[10px]">
                <Minus className="h-3 w-3 text-destructive" /> {stats.removed}
              </Badge>
              <span className="ml-auto text-muted-foreground">
                Base v{versions?.find((x: any) => x.id === versionA)?.version_number ?? "—"} →
                {" "}v{versions?.find((x: any) => x.id === versionB)?.version_number ?? "—"}
              </span>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-4 font-mono text-xs whitespace-pre-wrap">
                {isLoadingDiff && <Skeleton className="h-40 w-full" />}
                {!isLoadingDiff && diff.length === 0 && (
                  <div className="text-muted-foreground text-center py-10">
                    Selecione duas versões para ver as diferenças.
                  </div>
                )}
                {!isLoadingDiff && diff.length > 0 && stats.added === 0 && stats.removed === 0 && (
                  <div className="text-muted-foreground text-center py-10">
                    As versões selecionadas são idênticas.
                  </div>
                )}
                {!isLoadingDiff && diff.map((line, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      "px-2 py-0.5 rounded",
                      line.type === "added" && "bg-emerald-500/10 text-emerald-800 dark:text-emerald-300",
                      line.type === "removed" && "bg-destructive/10 text-destructive line-through",
                    )}
                  >
                    <span className="select-none opacity-60 mr-2">
                      {line.type === "added" ? "+" : line.type === "removed" ? "-" : " "}
                    </span>
                    {line.text || "\u00A0"}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
